import { Type } from "@angular/core";
import { Observable, of } from "rxjs";
import { mergeMap } from "rxjs/operators";
import { IUser } from ".";
import { CirculationFacade } from "../services/circulation.facade";
import { STEPTYPEINFORMATION } from "../step/types/constants";
import { CirculationId } from "./circulation";
import { Entity, EntityId } from "./entity";

export type StepId = EntityId;
export type StepStatus =
  | "Waiting"
  | "InProgress"
  | "Finished"
  | "Errored"
  | "Canceled"; // see table dbo.StepStatus
export type StepResult = "Confirmed" | "Rejected" | "Forwarded" | "NotRequired";

/** Default payload: title & comment */
export interface IDefaultStepPayloadData {
  title: string;
  comment: string;
}

export interface IStepPayload {
  data?: IDefaultStepPayloadData;
  stepType: string;
  shouldTheReportAlwaysBeSent?: boolean;
}

export interface IStepForwardData {
  comment: string;
  forwardTo: IUser;
  step: IStepInit;
}

export interface IStepOverrideForwardData extends IStepForwardData {
  currentResponsible: IUser;
}

export interface IStepResultData {
  result: StepResult;
  comment?: string;
}

export interface IStepAssignee {
  id: number;
  user: IUser;
  result?: StepResult;
  comment?: string;
  completionDate?: Date;
  forwardData?: {
    forwardedFromId?: number;
    forwardedToId?: number;
  };
}

export interface IStepTasks {
  assignees: IStepAssignee[];
  firstResponseApplies: boolean;
}

export interface IStepInit {
  tasks: IStepTasks;
  circulationId: CirculationId;
  dueDate?: Date;
  id: StepId;
  payload: IStepPayload;
  status: StepStatus;
  stepNo: number;
  permissions?: IStepPermissions;
}

export interface IStepPermissions {
  canAddDocuments?: boolean;
}

export interface IPermissionExtendedStep extends IStepInit {
  canEditStep: boolean;
  canWorkStep: boolean;
  canOverrideStep: boolean;
}

export interface IExtendedStep extends IStepInit {
  circulationName: string;
  title: string;
}

export interface ICirculationTemplateStep {
  tasks: {
    assignedTo: IUser[];
    firstResponseApplies: boolean;
  };
  stepNo: number;
  duration?: number;
  payload: IStepPayload;
  permissions?: IStepPermissions;
}

export class Step extends Entity<IStepInit> {
  constructor(props: IStepInit, id?: EntityId) {
    super(props, 0, id);
  }

  public static canEdit(
    step: IStepInit,
    user: IUser,
    circulationFacade: CirculationFacade,
    isAdmin: boolean
  ): Observable<boolean> {
    if (step.status !== "Waiting") {
      return of(false);
    }

    return circulationFacade.getCirculation(step.circulationId).pipe(
      mergeMap((c) => {
        return c.canEdit(user, isAdmin);
      })
    );
  }

  public static canOverride(
    step: IStepInit,
    user: IUser,
    circulationFacade: CirculationFacade,
    isAdmin: boolean
  ): Observable<boolean> {
    if (step.status !== "InProgress") {
      return of(false);
    }

    return circulationFacade.getCirculation(step.circulationId).pipe(
      mergeMap((c) => {
        return c.canOverride(user, isAdmin);
      })
    );
  }

  public canAddDocuments(user: IUser, isAdmin: boolean): Observable<boolean> {
    if (this.props.status !== "InProgress") {
      return of(false);
    }

    const userIsAssigned = this.props.tasks.assignees.some(
      (x) => !x.result && x.user.id == user.id
    );
    const canAddDocuments = this.props.permissions?.canAddDocuments || false;

    return of((userIsAssigned || isAdmin) && canAddDocuments);
  }

  public canEdit(
    user: IUser,
    circulationFacade: CirculationFacade,
    isAdmin: boolean
  ): Observable<boolean> {
    return Step.canEdit(this.props, user, circulationFacade, isAdmin);
  }

  public static getStepResult(props: IStepInit): StepResult {
    // if one task is rejected, the step is rejected
    if (props.tasks.assignees.some((x) => x.result == "Rejected")) {
      return "Rejected";
    }

    // if all tasks are NotRequired, the step was BotRequired
    if (props.tasks.assignees.every((x) => x.result == "NotRequired")) {
      return "NotRequired";
    }

    // if at least one is Confirmed and the rest are Confirmed or NotRequired
    if (props.tasks.assignees.some((x) => x.result == "Confirmed")) {
      const notForwardedAssignees = props.tasks.assignees.filter(
        (a) => a.result != "Forwarded" && a.result !== null
      );

      if (
        notForwardedAssignees.every(
          (x) => x.result == "Confirmed" || x.result == "NotRequired"
        )
      ) {
        return "Confirmed";
      }
    }

    return null;
  }

  public static getHasErrors(props: IStepInit): boolean {
    const stepNeedsDueDate = STEPTYPEINFORMATION.requiresDueDate(
      props.payload.stepType
    );
    const dueDateNotSet = props.dueDate == undefined;
    if (stepNeedsDueDate && dueDateNotSet) {
      return true;
    }

    const stepNeedsAssignees = STEPTYPEINFORMATION.requiresAssignees(
      props.payload.stepType
    );
    const userMissing =
      props.tasks.assignees.length < 1 ||
      props.tasks.assignees.some((a) => !a.user);
    if (stepNeedsAssignees && userMissing) {
      return true;
    }

    return false;
  }

  public get result(): StepResult {
    return Step.getStepResult(this.props);
  }

  public canWork(user: IUser): Observable<boolean> {
    return Step.canWork(this.props, user);
  }

  public static canWork(step: IStepInit, user: IUser): Observable<boolean> {
    if (step.status !== "InProgress") {
      return of(false);
    }

    return of(
      step.tasks.assignees.some((x) => !x.result && x.user.id == user.id)
    );
  }
}

export interface IStepTypeProviderDescription {
  createComponent: Type<unknown>;
  createTemplateComponent: Type<unknown>;
  description: string;
  editComponent: Type<unknown>;
  editTemplateComponent: Type<unknown>;
  id: string;
  name: string;
  workComponent: Type<unknown>;
}

export interface IReorderStepsData {
  circulationId: EntityId;
  steps: IReorderedStepData[];
}

export interface IReorderedStepData {
  originalOrder: number;
  dueDate?: Date;
  order: number;
  stepId: StepId;
}
